import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Compass,
  Heart,
  HeartPulse,
  Leaf,
  NotebookPen,
  RotateCcw,
  Sparkles,
  Sprout,
  Wind,
} from "lucide-react";
import { withMemberGate } from "@/lib/with-member-gate";
import { OptionPicker } from "@/components/reset30/OptionPicker";
import { JourneyTimeline } from "@/components/reset30/JourneyTimeline";
import { ProgressRing } from "@/components/reset30/ProgressRing";
import { ResetProgressDialog } from "@/components/reset30/ResetProgressDialog";
import {
  AFTER_OPTIONS,
  BODY_NOTICE_OPTIONS,
  CAPACITY_OPTIONS,
  DAYS,
  KEEP_OPTIONS,
  PRACTICE_SAFETY_NOTE,
  PROFILE_BODY_AREAS,
  PROFILE_HARDER,
  PROFILE_HELPS,
  PROFILE_STRESS_RESPONSES,
  STAGES,
  STATE_OPTIONS,
  TOOLKIT_SECTIONS,
  completedDays,
  currentDay,
  dayContent,
  mostHelpfulTools,
  percentComplete,
  stageForDay,
  type DayRecord,
  type NsrState,
  type ToolkitKey,
} from "@/lib/nervous-system-reset";
import {
  getResetProgress,
  saveResetProgress,
} from "@/lib/nervous-system-reset.functions";

const TITLE = "Reset & Regulate — 30-Day Nervous System Regulation Journey — Recovery Trauma™";
const DESC =
  "A gentle, self-paced 30-day programme to help you notice your nervous system, explore body-based practices and build your own personal regulation toolkit.";

export const Route = createFileRoute("/nervous-system-reset")({
  // ?day=N opens that day's practice straight away (used by the dashboard timeline).
  validateSearch: (search: Record<string, unknown>): { day?: number } => {
    const raw = Number(search["day"]);
    return Number.isInteger(raw) && raw >= 1 && raw <= 30 ? { day: raw } : {};
  },
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        property: "og:url",
        content: "https://recoverytrauma.com/nervous-system-reset",
      },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://recoverytrauma.com/nervous-system-reset",
      },
    ],
  }),
  component: withMemberGate(ResetProgrammePage, "Reset & Regulate"),
});

type View = "overview" | "profile" | "day" | "toolkit";

function ResetProgrammePage() {
  const qc = useQueryClient();
  const load = useServerFn(getResetProgress);
  const save = useServerFn(saveResetProgress);

  const {
    data,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["nsr-progress"],
    queryFn: () => load(),
    // Progress barely changes within a session — return visits render instantly.
    staleTime: 60_000,
    // Ride out a flaky mobile connection before showing the member an error.
    retry: (failureCount, error) => {
      const message = error instanceof Error ? error.message : "";
      if (/\b(401|403|404)\b|unauthor/i.test(message)) return false;
      return failureCount < 2;
    },
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 6000),
  });

  const [state, setState] = useState<NsrState>({});
  const hydrated = useRef(false);

  useEffect(() => {
    if (data && !hydrated.current) {
      setState((data.state ?? {}) as NsrState);
      hydrated.current = true;
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (next: NsrState) => save({ data: { state: next } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["nsr-progress"] }),
  });

  function persist(updater: (prev: NsrState) => NsrState) {
    setState((prev) => {
      const next = updater(prev);
      saveMutation.mutate(next);
      return next;
    });
  }

  const done = completedDays(state);
  const today = currentDay(state);
  const pct = percentComplete(state);

  const { day: dayParam } = Route.useSearch();

  const [view, setView] = useState<View>(dayParam ? "day" : "overview");
  const [selectedDay, setSelectedDay] = useState(dayParam ?? 1);

  useEffect(() => {
    if (!hydrated.current) return;
    // A ?day=N link opens that day; otherwise land on the day they're up to.
    if (dayParam) {
      setSelectedDay(dayParam);
      setView("day");
    } else {
      setSelectedDay(currentDay(state));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated.current, dayParam]);

  // Keep the open day in the address (?day=N) so returning from a tool —
  // via "Back to Reset & Regulate" or the phone/browser back — reopens the
  // same day instead of the overview.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const want = view === "day" ? String(selectedDay) : null;
    if (url.searchParams.get("day") === want) return;
    if (want) url.searchParams.set("day", want);
    else url.searchParams.delete("day");
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
  }, [view, selectedDay]);

  function openDay(day: number) {
    setSelectedDay(day);
    setView("day");
    if (typeof window !== "undefined") window.scrollTo({ top: 0 });
  }

  if (isLoading) {
    return (
      <Shell>
        <div className="space-y-4" aria-busy="true" aria-live="polite">
          <span className="sr-only">Loading your progress</span>
          <div className="h-32 animate-pulse rounded-3xl bg-muted" aria-hidden />
          <div className="h-10 w-2/3 animate-pulse rounded-2xl bg-muted" aria-hidden />
          <div className="grid gap-3 sm:grid-cols-2">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-24 animate-pulse rounded-3xl bg-muted"
                aria-hidden
              />
            ))}
          </div>
        </div>
      </Shell>
    );
  }

  if (isError) {
    return (
      <Shell>
        <div className="rounded-3xl border border-border bg-background/70 p-6 text-sm text-foreground/80">
          <p>
            Your 30-Day Reset progress didn&apos;t load just now. Nothing has
            been lost — this is usually a connection hiccup.
          </p>
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            className="mt-4 inline-flex min-h-[48px] items-center justify-center rounded-full border border-gold/30 px-5 text-sm font-medium text-foreground/80 transition hover:bg-gold-light/30 disabled:opacity-60"
          >
            {isFetching ? "Trying again…" : "Try again"}
          </button>
        </div>
      </Shell>
    );
  }

  if (view === "profile") {
    return (
      <Shell onBack={() => setView("overview")}>
        <ProfileWizard
          state={state}
          onSave={(profile) =>
            persist((p) => ({
              ...p,
              startedAt: p.startedAt ?? new Date().toISOString(),
              profile: { ...profile, completedAt: new Date().toISOString() },
            }))
          }
          onDone={() => setView("overview")}
        />
      </Shell>
    );
  }

  if (view === "toolkit") {
    return (
      <Shell onBack={() => setView("overview")}>
        <ToolkitView state={state} />
      </Shell>
    );
  }

  if (view === "day") {
    return (
      <Shell onBack={() => setView("overview")}>
        <DayView
          key={selectedDay}
          day={selectedDay}
          record={state.days?.[String(selectedDay)] ?? {}}
          onSaveRecord={(rec) =>
            persist((p) => ({
              ...p,
              startedAt: p.startedAt ?? new Date().toISOString(),
              days: { ...(p.days ?? {}), [String(selectedDay)]: rec },
            }))
          }
          onComplete={(rec, saved, toolkitAdds) =>
            persist((p) => {
              const toolkit = { ...(p.toolkit ?? {}) };
              for (const [key, values] of Object.entries(toolkitAdds)) {
                const k = key as ToolkitKey;
                toolkit[k] = Array.from(
                  new Set([...(toolkit[k] ?? []), ...values]),
                );
              }
              return {
                ...p,
                startedAt: p.startedAt ?? new Date().toISOString(),
                days: {
                  ...(p.days ?? {}),
                  [String(selectedDay)]: {
                    ...rec,
                    completedAt: new Date().toISOString(),
                  },
                },
                savedTools: saved
                  ? Array.from(new Set([...(p.savedTools ?? []), saved]))
                  : p.savedTools,
                toolkit,
              };
            })
          }
          onNext={() => {
            if (selectedDay < 30) openDay(selectedDay + 1);
            else setView("overview");
          }}
          onBackToOverview={() => setView("overview")}
        />
      </Shell>
    );
  }

  async function handleReset() {
    const next: NsrState = {
      startedAt: new Date().toISOString(),
      profile: state.profile,
      savedTools: state.savedTools,
      toolkit: state.toolkit,
      toolkitSavedAt: state.toolkitSavedAt,
    };
    await save({ data: { state: next } });
    setState(next);
    setSelectedDay(1);
    qc.invalidateQueries({ queryKey: ["nsr-progress"] });
  }

  const resetAction = (
    <ResetProgressDialog
      trigger={
        <button
          type="button"
          className="inline-flex min-h-10 cursor-pointer items-center gap-1.5 rounded-full border border-border/60 px-4 text-xs text-muted-foreground transition hover:border-[var(--gold-deep)]/50 hover:text-foreground"
        >
          <RotateCcw className="h-3.5 w-3.5" /> Reset progress
        </button>
      }
      onConfirm={handleReset}
    />
  );

  return (
    <Shell>
      {/* Hero */}
      <section
        className="overflow-hidden rounded-3xl border border-[var(--gold-deep)]/25 p-6 sm:p-8"
        style={{
          background:
            "linear-gradient(150deg, color-mix(in oklab, var(--qa-gold-tint) 85%, var(--card)) 0%, var(--card) 62%)",
        }}
      >
        <div className="flex items-start gap-4">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
            <Leaf className="h-5 w-5 text-foreground" />
          </span>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
              Regulate
            </p>
            <h1 className="font-serif text-2xl leading-tight text-foreground sm:text-[2rem]">
              Reset &amp; Regulate
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              30-Day Nervous System Regulation Journey
            </p>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-5">
          <ProgressRing percent={pct} day={today} />
          <div className="min-w-0">
            <p className="font-display text-[15px] text-foreground">
              Day {today} of 30 · {pct}% complete
            </p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {done.length === 0
                ? "Nothing to catch up on — you can begin whenever you're ready."
                : `${done.length} ${done.length === 1 ? "day" : "days"} noted so far. Pause or repeat any day.`}
            </p>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-[var(--gradient-gold)] transition-all duration-700"
                style={{ width: `${Math.max(pct, 2)}%` }}
              />
            </div>
          </div>
        </div>

        <div className="relative z-10 mt-6">
          <button
            type="button"
            className="btn-gold min-h-12 w-full cursor-pointer px-6 sm:w-auto"
            onClick={() => openDay(today)}
          >
            {done.length === 0 ? "Begin Day 1" : `Continue Day ${today}`}
            <ArrowRight className="h-4 w-4" />
          </button>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="inline-flex min-h-10 cursor-pointer items-center gap-1.5 rounded-full border border-gold/50 bg-card/80 px-4 text-xs text-foreground transition hover:bg-gold-light/40"
              onClick={() => setView("toolkit")}
            >
              <Sparkles className="h-3.5 w-3.5" /> My toolkit
            </button>
            {!state.profile?.completedAt && !state.profile?.skipped && (
              <button
                type="button"
                className="inline-flex min-h-10 cursor-pointer items-center gap-1.5 rounded-full border border-border/60 px-4 text-xs text-muted-foreground transition hover:text-foreground"
                onClick={() => setView("profile")}
              >
                <Compass className="h-3.5 w-3.5" /> My starting point
              </button>
            )}
            {resetAction}
          </div>
        </div>
      </section>

      {/* What is it */}
      <section className="mt-5 grid gap-4 rounded-3xl border border-border/50 bg-card p-5 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-start sm:p-6">
        <span
          className="grid h-12 w-12 place-items-center rounded-2xl"
          style={{
            background: "color-mix(in oklab, var(--qa-sage-ink) 14%, var(--card))",
            color: "var(--qa-sage-ink)",
          }}
        >
          <Sprout className="h-6 w-6" />
        </span>
        <div className="min-w-0">
          <h2 className="font-serif text-lg text-foreground">
            What is the 30-Day Reset?
          </h2>
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
            A self-paced 30-day programme to help you notice your
            nervous-system patterns, practise regulation gently, and gradually
            build a set of tools that genuinely work for you. A few minutes a
            day is enough, and every practice is optional.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="mt-5">
        <h2 className="px-1 font-serif text-lg text-foreground">How it works</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          {HOW_IT_WORKS.map((step, i) => (
            <div
              key={step.label}
              className="rounded-2xl border border-border/50 p-4"
              style={{
                background: `linear-gradient(160deg, color-mix(in oklab, var(${step.tint}-tint) 70%, var(--card)) 0%, var(--card) 80%)`,
              }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-full"
                  style={{
                    background: `color-mix(in oklab, var(${step.tint}-ink) 16%, var(--card))`,
                    color: `var(${step.tint}-ink)`,
                  }}
                >
                  <step.icon className="h-4 w-4" />
                </span>
                <span className="text-[11px] uppercase tracking-widest text-muted-foreground">
                  Step {i + 1}
                </span>
              </div>
              <p className="mt-2 font-display text-[15px] text-foreground">
                {step.label}
              </p>
              <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                {step.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Journey */}
      <section
        className="mt-5 rounded-3xl border border-border/50 p-5 sm:p-6"
        style={{
          background:
            "linear-gradient(180deg, color-mix(in oklab, var(--qa-gold-tint) 45%, var(--card)) 0%, var(--card) 45%)",
        }}
      >
        <h2 className="font-serif text-xl text-foreground">Your journey</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Four gentle phases. Nothing is locked — move at your own pace and
          revisit any day.
        </p>
        <div className="mt-5">
          <JourneyTimeline
            completed={done}
            current={today}
            onSelect={openDay}
          />
        </div>
      </section>

      {(state.savedTools?.length ?? 0) > 0 && (
        <section className="mt-5 rounded-3xl border border-border/50 bg-card p-5 sm:p-6">
          <h2 className="font-serif text-lg text-foreground">
            Tools you've saved
          </h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {(state.savedTools ?? []).map((t) => (
              <span
                key={t}
                className="rounded-full bg-[var(--gradient-gold)] px-3 py-1.5 text-[13px] text-foreground"
              >
                ♡ {t}
              </span>
            ))}
          </div>
        </section>
      )}

      <section className="mt-5 rounded-2xl border border-border/50 bg-muted/40 p-4 text-xs leading-relaxed text-muted-foreground">
        This programme is educational support for self-understanding. It isn't
        therapy, diagnosis or medical treatment, and it doesn't claim to cure
        anything. If you feel unsafe or in crisis, please reach out to a
        professional or an emergency service where you are.{" "}
        <Link to="/crisis" className="underline">
          Crisis support
        </Link>
      </section>
    </Shell>
  );
}

const HOW_IT_WORKS = [
  {
    label: "Check in",
    desc: "A few taps to notice how your body feels today.",
    icon: HeartPulse,
    tint: "--qa-peach",
  },
  {
    label: "Try today's practice",
    desc: "One short, optional practice — gentler versions always offered.",
    icon: Wind,
    tint: "--qa-teal",
  },
  {
    label: "Reflect & save what helped",
    desc: "Keep anything that worked in your own toolkit.",
    icon: NotebookPen,
    tint: "--qa-sage",
  },
] as const;

/* ------------------------------- Layout ------------------------------- */

function Shell({
  children,
  onBack,
}: {
  children: React.ReactNode;
  onBack?: () => void;
}) {
  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:py-10">
      <div className="mb-5">
        {onBack ? (
          <button
            onClick={onBack}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Programme overview
          </button>
        ) : (
          <Link
            to="/somatic"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Regulate
          </Link>
        )}
      </div>
      {children}
    </div>
  );
}

/* ---------------------------- Profile wizard ---------------------------- */

function ProfileWizard({
  state,
  onSave,
  onDone,
}: {
  state: NsrState;
  onSave: (profile: NonNullable<NsrState["profile"]>) => void;
  onDone: () => void;
}) {
  const p = state.profile ?? {};
  const [stressResponses, setStress] = useState<string[]>(
    p.stressResponses ?? [],
  );
  const [bodyAreas, setBody] = useState<string[]>(p.bodyAreas ?? []);
  const [helps, setHelps] = useState<string[]>(p.helps ?? []);
  const [harder, setHarder] = useState<string[]>(p.harder ?? []);
  const [hope, setHope] = useState(p.hope ?? "");

  return (
    <div className="rounded-3xl border border-border/50 bg-card p-6 sm:p-8">
      <h2 className="font-serif text-2xl text-foreground">
        My starting point
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Entirely optional, and there are no wrong answers. This just helps the
        programme reflect your own words back to you later on.
      </p>

      <div className="mt-6 space-y-7">
        <OptionPicker
          question="When I'm stressed, I usually…"
          options={PROFILE_STRESS_RESPONSES}
          value={stressResponses}
          onChange={setStress}
          multi
          allowCustom
        />
        <OptionPicker
          question="Where do you feel stress in your body?"
          options={PROFILE_BODY_AREAS}
          value={bodyAreas}
          onChange={setBody}
          multi
          allowCustom
        />
        <OptionPicker
          question="What usually helps you feel a bit better?"
          options={PROFILE_HELPS}
          value={helps}
          onChange={setHelps}
          multi
          allowCustom
        />
        <OptionPicker
          question="What tends to make things harder?"
          options={PROFILE_HARDER}
          value={harder}
          onChange={setHarder}
          multi
          allowCustom
        />
        <div>
          <p className="font-display text-[15px] text-foreground">
            What would you like to feel more of?
          </p>
          <textarea
            value={hope}
            onChange={(e) => setHope(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="In your own words…"
            className="mt-3 w-full rounded-xl border border-border/60 bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-[var(--gold-deep)]"
          />
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <button
          className="btn-gold"
          onClick={() => {
            onSave({ stressResponses, bodyAreas, helps, harder, hope });
            onDone();
          }}
        >
          Save and continue <ArrowRight className="h-4 w-4" />
        </button>
        <button
          className="btn-gold-outline"
          onClick={() => {
            onSave({ skipped: true });
            onDone();
          }}
        >
          Skip this
        </button>
      </div>
    </div>
  );
}

/* -------------------------------- Day view -------------------------------- */

function DayView({
  day,
  record,
  onSaveRecord,
  onComplete,
  onNext,
  onBackToOverview,
}: {
  day: number;
  record: DayRecord;
  onSaveRecord: (rec: DayRecord) => void;
  onComplete: (
    rec: DayRecord,
    savedTool: string | null,
    toolkitAdds: Partial<Record<ToolkitKey, string[]>>,
  ) => void;
  onNext: () => void;
  onBackToOverview: () => void;
}) {
  const content = dayContent(day);
  const stage = stageForDay(day);

  const [checkInState, setCheckInState] = useState<string[]>(
    record.checkInState ? [record.checkInState] : [],
  );
  const [checkInBody, setCheckInBody] = useState<string[]>(
    record.checkInBody ?? [],
  );
  const [capacity, setCapacity] = useState<string[]>(
    record.capacity ? [record.capacity] : [],
  );
  const [answers, setAnswers] = useState<Record<string, string[]>>(
    record.answers ?? {},
  );
  const [reflection, setReflection] = useState(record.reflection ?? "");
  const [after, setAfter] = useState<string[]>(
    record.after ? [record.after] : [],
  );
  const [keep, setKeep] = useState<string[]>(record.keep ? [record.keep] : []);
  const [justCompleted, setJustCompleted] = useState(false);

  const gentler = capacity[0] === CAPACITY_OPTIONS[1].label;
  const checkInOnly = capacity[0] === CAPACITY_OPTIONS[2].label;
  const skipping = capacity[0] === CAPACITY_OPTIONS[3].label;

  const currentRecord: DayRecord = useMemo(
    () => ({
      ...record,
      checkInState: checkInState[0],
      checkInBody,
      capacity: capacity[0],
      answers,
      reflection,
      after: after[0],
      keep: keep[0],
    }),
    [record, checkInState, checkInBody, capacity, answers, reflection, after, keep],
  );

  const toolkitAdds = useMemo(() => {
    const adds: Partial<Record<ToolkitKey, string[]>> = {};
    for (const picker of content.pickers ?? []) {
      if (!picker.toolkitKey) continue;
      const vals = answers[picker.id] ?? [];
      if (vals.length) {
        adds[picker.toolkitKey] = [
          ...(adds[picker.toolkitKey] ?? []),
          ...vals,
        ];
      }
    }
    return adds;
  }, [answers, content.pickers]);

  if (justCompleted) {
    return (
      <div className="rounded-3xl border border-border/50 bg-card p-8 text-center">
        <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[var(--gradient-gold)]">
          <Check className="h-6 w-6 text-foreground" />
        </span>
        <h2 className="mt-4 font-serif text-2xl text-foreground">
          Day {day} noted
        </h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
          Thank you for showing up for yourself today. There's nothing else you
          need to do.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {day < 30 && (
            <button className="btn-gold" onClick={onNext}>
              Day {day + 1} <ArrowRight className="h-4 w-4" />
            </button>
          )}
          <button className="btn-gold-outline" onClick={onBackToOverview}>
            Back to overview
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <header className="rounded-3xl border border-border/50 bg-card p-6">
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
          Day {day} of 30 · {stage.label}
        </p>
        <h1 className="mt-1 font-serif text-2xl text-foreground sm:text-3xl">
          {content.title}
        </h1>
        {record.completedAt && (
          <p className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Check className="h-3.5 w-3.5" /> You've completed this day before —
            you're welcome to repeat it.
          </p>
        )}
      </header>

      {/* Check-in */}
      <section className="rounded-3xl border border-border/50 bg-card p-5 sm:p-6">
        <h2 className="font-serif text-lg text-foreground">
          Today's check-in
        </h2>
        <div className="mt-4 space-y-6">
          <OptionPicker
            question="How is your body feeling right now?"
            options={STATE_OPTIONS.map((s) => `${s.emoji} ${s.label}`)}
            value={checkInState}
            onChange={setCheckInState}
          />
          <OptionPicker
            question="What do you notice in your body?"
            options={BODY_NOTICE_OPTIONS}
            value={checkInBody}
            onChange={setCheckInBody}
            multi
            allowCustom
          />
          <OptionPicker
            question="What feels possible today?"
            options={CAPACITY_OPTIONS.map((c) => c.label)}
            value={capacity}
            onChange={setCapacity}
          />
        </div>
      </section>

      {skipping ? (
        <section className="rounded-3xl border border-border/50 bg-card p-6">
          <p className="text-sm leading-relaxed text-muted-foreground">
            Skipping today is a valid choice, and choosing it is itself a way of
            listening to yourself. Your check-in is saved. The day will be here
            when you want it.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              className="btn-gold"
              onClick={() => {
                onSaveRecord(currentRecord);
                onBackToOverview();
              }}
            >
              Save my check-in
            </button>
            <Link to="/grounding" className="btn-gold-outline">
              One-minute grounding instead
            </Link>
          </div>
        </section>
      ) : (
        <>
          {/* Teaching */}
          <section className="rounded-3xl border border-border/50 bg-card p-5 sm:p-6">
            {content.intro.map((para, i) => (
              <p
                key={i}
                className="mt-2 text-sm leading-relaxed text-muted-foreground first:mt-0"
              >
                {para}
              </p>
            ))}
          </section>

          {/* Practice */}
          {content.practice && !checkInOnly && (
            <section className="rounded-3xl border border-[var(--gold-deep)]/30 bg-card p-5 sm:p-6">
              <div className="flex items-center gap-2">
                <Wind className="h-4 w-4 text-[var(--gold-deep)]" />
                <h2 className="font-serif text-lg text-foreground">
                  {content.practice.title}
                </h2>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {content.practice.minutes} · {content.practice.purpose}
              </p>

              {gentler && content.practice.gentler && (
                <div className="mt-4 rounded-xl bg-muted/50 p-3 text-sm text-foreground">
                  <span className="font-display">A gentler version: </span>
                  {content.practice.gentler}
                </div>
              )}

              <ol className="mt-4 space-y-2">
                {content.practice.steps.map((s, i) => (
                  <li key={i} className="flex gap-3 text-sm text-muted-foreground">
                    <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-muted text-[11px] text-foreground">
                      {i + 1}
                    </span>
                    <span className="leading-relaxed">{s}</span>
                  </li>
                ))}
              </ol>

              {content.practice.tips && (
                <ul className="mt-4 space-y-1">
                  {content.practice.tips.map((t) => (
                    <li key={t} className="text-xs text-muted-foreground">
                      · {t}
                    </li>
                  ))}
                </ul>
              )}

              {content.practice.seatedAlternative && (
                <p className="mt-4 rounded-xl bg-muted/50 p-3 text-xs leading-relaxed text-foreground">
                  <span className="font-display">Seated option: </span>
                  {content.practice.seatedAlternative}
                </p>
              )}

              <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
                {PRACTICE_SAFETY_NOTE}
              </p>
            </section>
          )}

          {content.optional && (
            <section className="rounded-2xl border border-border/50 bg-muted/40 p-4 text-sm leading-relaxed text-muted-foreground">
              {content.optional}
            </section>
          )}

          {/* Interactive pickers */}
          {(content.pickers?.length ?? 0) > 0 && (
            <section className="rounded-3xl border border-border/50 bg-card p-5 sm:p-6">
              <div className="space-y-7">
                {content.pickers!.map((picker) => (
                  <OptionPicker
                    key={picker.id}
                    question={picker.question}
                    note={picker.note}
                    options={picker.options}
                    multi={picker.multi}
                    allowCustom={picker.allowCustom}
                    value={answers[picker.id] ?? []}
                    onChange={(next) =>
                      setAnswers((a) => ({ ...a, [picker.id]: next }))
                    }
                  />
                ))}
              </div>
            </section>
          )}

          {/* Linked tools */}
          {(content.tools?.length ?? 0) > 0 && (
            <section className="rounded-3xl border border-border/50 bg-card p-5 sm:p-6">
              <h2 className="font-serif text-lg text-foreground">
                Tools for today
              </h2>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {content.tools!.map((t) => (
                  <a
                    key={t.to}
                    href={t.to}
                    className="rounded-2xl border border-border/60 p-3 transition hover:border-[var(--gold-deep)]/60"
                  >
                    <p className="font-display text-sm text-foreground">
                      {t.label}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {t.desc}
                    </p>
                  </a>
                ))}
              </div>
            </section>
          )}

          {/* Reflection */}
          {(content.reflection || content.journalPrompt) && (
            <section className="rounded-3xl border border-border/50 bg-card p-5 sm:p-6">
              <div className="flex items-center gap-2">
                <NotebookPen className="h-4 w-4 text-[var(--gold-deep)]" />
                <h2 className="font-serif text-lg text-foreground">
                  Reflection
                </h2>
              </div>
              {content.reflection && (
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {content.reflection}
                </p>
              )}
              {content.reflectionOptions ? (
                <div className="mt-3">
                  <OptionPicker
                    question=""
                    options={content.reflectionOptions}
                    value={reflection ? [reflection] : []}
                    onChange={(v) => setReflection(v[0] ?? "")}
                  />
                </div>
              ) : (
                <textarea
                  value={reflection}
                  onChange={(e) => setReflection(e.target.value)}
                  rows={4}
                  maxLength={5000}
                  placeholder="Optional — a few words is plenty."
                  className="mt-3 w-full rounded-xl border border-border/60 bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-[var(--gold-deep)]"
                />
              )}
              {content.journalPrompt && (
                <a
                  href={`/journal?prompt=${encodeURIComponent(content.journalPrompt)}`}
                  className="mt-3 inline-flex items-center gap-1 text-xs text-[var(--gold-deep)] underline"
                >
                  Write more in my journal <ArrowRight className="h-3 w-3" />
                </a>
              )}
            </section>
          )}

          {/* After the practice */}
          <section className="rounded-3xl border border-border/50 bg-card p-5 sm:p-6">
            <div className="flex items-center gap-2">
              <Heart className="h-4 w-4 text-[var(--gold-deep)]" />
              <h2 className="font-serif text-lg text-foreground">
                After today
              </h2>
            </div>
            <div className="mt-4 space-y-6">
              <OptionPicker
                question="How do you feel now?"
                options={AFTER_OPTIONS}
                value={after}
                onChange={setAfter}
                allowCustom
              />
              {content.practice && (
                <OptionPicker
                  question="Would you like to keep this practice?"
                  options={KEEP_OPTIONS.map((k) => k.label)}
                  value={keep}
                  onChange={setKeep}
                />
              )}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                className="btn-gold"
                onClick={() => {
                  const savedTool =
                    keep[0] === KEEP_OPTIONS[0].label && content.practice
                      ? content.practice.title
                      : null;
                  onComplete(currentRecord, savedTool, toolkitAdds);
                  setJustCompleted(true);
                  if (typeof window !== "undefined")
                    window.scrollTo({ top: 0 });
                }}
              >
                Mark day {day} complete <Check className="h-4 w-4" />
              </button>
              <button
                className="btn-gold-outline"
                onClick={() => {
                  onSaveRecord(currentRecord);
                  onBackToOverview();
                }}
              >
                Save and come back later
              </button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

/* -------------------------------- Toolkit -------------------------------- */

function ToolkitView({ state }: { state: NsrState }) {
  const helpful = mostHelpfulTools(state);
  const sections = TOOLKIT_SECTIONS.filter(
    (s) => (state.toolkit?.[s.key]?.length ?? 0) > 0,
  );
  const empty = sections.length === 0 && helpful.length === 0;

  return (
    <div className="space-y-5">
      <header className="rounded-3xl border border-border/50 bg-card p-6">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-full bg-[var(--gradient-gold)]">
            <Sparkles className="h-5 w-5 text-foreground" />
          </span>
          <div>
            <h1 className="font-serif text-2xl text-foreground">
              My Nervous System Toolkit
            </h1>
            <p className="text-xs text-muted-foreground">
              Built from your own answers. Private to you.
            </p>
          </div>
        </div>
      </header>

      {empty ? (
        <section className="rounded-3xl border border-border/50 bg-card p-6">
          <p className="text-sm leading-relaxed text-muted-foreground">
            Your toolkit fills itself as you move through the 30 days. Days 4,
            18, 21, 27, 28 and 29 all add to it — and you can revisit any of
            them at any time.
          </p>
        </section>
      ) : (
        <>
          {helpful.length > 0 && (
            <section className="rounded-3xl border border-[var(--gold-deep)]/30 bg-card p-5 sm:p-6">
              <h2 className="font-serif text-lg text-foreground">
                What seems to help you most
              </h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {helpful.map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-[var(--gradient-gold)] px-3 py-1.5 text-[13px] text-foreground"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </section>
          )}

          <section className="grid gap-3 sm:grid-cols-2">
            {sections.map((s) => (
              <div
                key={s.key}
                className="rounded-2xl border border-border/50 bg-card p-4"
              >
                <p className="font-display text-sm text-foreground">
                  {s.label}
                </p>
                <ul className="mt-2 space-y-1">
                  {(state.toolkit?.[s.key] ?? []).map((v) => (
                    <li key={v} className="text-xs text-muted-foreground">
                      · {v}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        </>
      )}

      <section className="rounded-2xl border border-border/50 bg-muted/40 p-4 text-xs leading-relaxed text-muted-foreground">
        Your toolkit is a living list. What helps can change with sleep, stress
        and season — you can update it any time by revisiting a day.
      </section>
    </div>
  );
}

export const _days = DAYS.length;
